import React, { useEffect, useState } from "react";
import {
    X,
    Music2,
    Calendar,
    BadgeCheck,
    Clock,
    ExternalLink,
    Info,
    Disc,
    Loader2
} from "lucide-react";
import { useDiscography } from "../../services/admin/DiscographyServices";

export default function ViewDiscographyModal({ itemId, isOpen, onClose }) {
    const { fetchMusicById, loading } = useDiscography();
    const [item, setItem] = useState(null);

    useEffect(() => {
        if (isOpen && itemId) {
            const loadItem = async () => {
                const data = await fetchMusicById(itemId);
                setItem(data);
            };
            loadItem();
        } else if (!isOpen) {
            setItem(null);
        }
    }, [isOpen, itemId, fetchMusicById]);

    if (!isOpen) return null;

    const getStatusStyle = (status) => {
        return status === 'active'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
            : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
    };

    // Parsing streaming URLs if they are stringified JSON
    let streamingLinks = {};
    if (item?.streaming) {
        try {
            streamingLinks = typeof item.streaming === 'string' ? JSON.parse(item.streaming) : item.streaming;
        } catch (e) {
            console.error("Failed to parse streaming links", e);
        }
    } else if (item) {
        // Handle individual fields if returned separately
        streamingLinks = {
            spotify: item.spotify_url,
            apple: item.apple_music_url,
            soundcloud: item.soundcloud_url,
            audiomack: item.audiomack_url
        };
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl overflow-y-auto transform transition-all border border-black/5 dark:border-white/5">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white/80 dark:bg-[#1A1A1A]/80 backdrop-blur-md px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                    <h2 className="text-xl font-normal text-black dark:text-white" style={{ fontFamily: "Playfair Display, serif" }}>
                        Release Details
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-full text-black hover:text-black/70 dark:text-white/60 dark:hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {loading || !item ? (
                    <div className="p-20 flex flex-col items-center justify-center gap-4">
                        <Loader2 className="animate-spin text-jazz-brown" size={48} />
                        <p className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Fetching details...</p>
                    </div>
                ) : (
                    <div className="p-6 sm:p-8 animate-in fade-in duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                            {/* Cover Art Column */}
                            <div className="md:col-span-5 space-y-6">
                                <div className="aspect-square w-full rounded-2xl bg-[#F5F5F5] dark:bg-zinc-800 border border-black/5 overflow-hidden shadow-lg shadow-black/5">
                                    {item.cover_path ? (
                                        <img
                                            src={item.cover_path}
                                            alt={item.title}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Music2 size={48} className="text-black/10" />
                                        </div>
                                    )}
                                </div>

                                <div className="bg-jazz-brown/5 p-4 rounded-xl border border-jazz-brown/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-black dark:text-white/50 uppercase tracking-widest">Metadata</span>
                                        <Disc size={14} className="text-jazz-brown" />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-black dark:text-white/50 uppercase tracking-wider font-bold">Added On</span>
                                            <span className="text-black dark:text-white/60">{item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Details Column */}
                            <div className="md:col-span-7 space-y-8">
                                <div className="space-y-4">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className="px-2 py-1 bg-black text-white dark:bg-white dark:text-black rounded text-xs font-bold uppercase tracking-[0.2em]">
                                            {item.category || item.type}
                                        </span>
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusStyle(item.status)}`}>
                                            {item.status === 'active' ? <BadgeCheck size={12} /> : <Clock size={12} />}
                                            {item.status}
                                        </span>
                                    </div>

                                    <h1 className="text-4xl font-bold text-black dark:text-white leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                                        {item.title}
                                    </h1>

                                    <div className="flex items-center gap-2 text-jazz-brown font-medium">
                                        <Calendar size={16} />
                                        <span className="text-sm uppercase tracking-widest font-bold">Released in {item.year}</span>
                                    </div>
                                </div>

                                {/* Description */}
                                {item.description && (
                                    <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                        <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-3">Liner Notes / Description</h3>
                                        <div className="text-black dark:text-white/70 text-base leading-relaxed font-serif italic whitespace-pre-wrap">
                                            "{item.description}"
                                        </div>
                                    </div>
                                )}

                                {/* Streaming Links */}
                                <div className="pt-6 border-t border-black/5 dark:border-white/5">
                                    <h3 className="text-xs font-bold text-black/60 uppercase tracking-[0.2em] mb-4">Availability & Streaming</h3>
                                    <div className="grid grid-cols-2 gap-3 font-poppins">
                                        {Object.entries(streamingLinks).map(([platform, url]) => {
                                            if (!url) return null;
                                            return (
                                                <a
                                                    key={platform}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-3 p-3 bg-white dark:bg-white/5 border border-black/5 rounded-xl hover:border-jazz-brown transition-all group"
                                                >
                                                    <div className="w-8 h-8 rounded-lg bg-gray-50 dark:bg-zinc-800 flex items-center justify-center text-jazz-brown group-hover:bg-jazz-brown group-hover:text-white transition-colors">
                                                        <ExternalLink size={14} />
                                                    </div>
                                                    <span className="text-xs font-bold uppercase tracking-widest text-black hover:text-black/70 dark:text-white/60 dark:group-hover:text-white">
                                                        {platform}
                                                    </span>
                                                </a>
                                            );
                                        })}
                                        {Object.values(streamingLinks).every(v => !v) && (
                                            <div className="col-span-2 py-4 px-6 bg-gray-50 dark:bg-white/5 rounded-xl border border-dashed border-black/10 text-center">
                                                <p className="text-xs text-black/60 font-bold uppercase tracking-widest italic">No streaming links provided</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 pt-8 border-t border-black/5 dark:border-white/5">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl border border-black/5">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-jazz-brown/10 flex items-center justify-center text-jazz-brown">
                                        <Info size={18} />
                                    </div>
                                    <p className="text-xs text-black/70 dark:text-white/50 font-bold uppercase tracking-widest leading-relaxed max-w-[400px]">
                                        This release is currently {item.status === 'active' ? 'visible' : 'hidden'} on the public website. Use the discography editor to modify its visibility or content.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sticky Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-[#1A1A1A] p-6 border-t border-black/5 dark:border-white/5">
                    <button
                        onClick={onClose}
                        className="w-full py-4 bg-black text-white rounded-xl text-xs font-bold tracking-widest hover:bg-jazz-brown transition-all uppercase shadow-lg shadow-black/10"
                    >
                        Close Details
                    </button>
                </div>
            </div>
        </div>
    );
}
